import React, { memo } from 'react'
import InfoIcon from 'transfix-icons/build/InfoIcon'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { RelativeDateTime } from 'transfix-ui/components/RelativeDateTime'
import { IPhoneSettingsProps } from 'apps/user/apiTypes/phoneSettings'
import { Card, Tooltip, Tag, TAG_STYLE, TAG_SIZE } from 'transfix-ui'

export interface IPermissionCardProps extends Pick<IPhoneSettingsProps, 'settings' | 'lastPingAt'> {
  errorMessage?: string
}

export const PermissionCard = memo(
  ({ errorMessage, lastPingAt, settings }: IPermissionCardProps) => {
    if (errorMessage || Object.entries(settings).length === 0) {
      return (
        <Card title='Permission & Activities'>
          <p>
            <InlineMessage
              message={errorMessage || 'Only available for pilot carriers'}
              type={INLINE_MESSAGE_VARIANTS.ALERT}
              fullWidth
            />
          </p>
        </Card>
      )
    }

    const locationPermissionLabel = (
      <span>
        {settings?.locationPermission?.label ?? 'Unknown'}{' '}
        <InfoIcon size='small' color='skyBlue6' />
      </span>
    )

    const pushPermissionLabel = (
      <span>
        {settings?.pushNotificationPermission?.label ?? 'Unknown'}{' '}
        <InfoIcon size='small' color='skyBlue6' />
      </span>
    )

    const lastLabel = ({ label }: { label: string }) => (
      <span className='mr1 text-secondary'>
        Last {label} <InfoIcon size='small' color='skyBlue6' /> :
      </span>
    )

    const getTimeSinceLastAction = ({ actionAt }: { actionAt?: string }) => {
      if (actionAt) {
        return <RelativeDateTime datetime={actionAt} inSentence />
      }
      return ' -- '
    }

    const getPermissionTagStyle = ({ labelType }: { labelType?: string }) => {
      if (labelType === 'success') {
        return TAG_STYLE.success
      }
      if (labelType === 'warning') {
        return TAG_STYLE.warning
      }
      return TAG_STYLE.error
    }

    const getPrettyStatusLabel = ({ status }: { status?: string }) => {
      if (status === 'turned_off') {
        return 'turned off'
      }
      if (status === 'background') {
        return 'in background'
      }
      if (status === 'foreground') {
        return 'opened'
      }
      return 'not setup'
    }

    const locationPermissionTagStyle = getPermissionTagStyle({
      labelType: settings?.locationPermission?.labelType,
    })

    const pushPermissionTagStyle = getPermissionTagStyle({
      labelType: settings?.pushNotificationPermission?.labelType,
    })

    const trackingStatus = settings?.lastTrackingStartedAt ? 'On' : 'Off'

    return (
      <Card title='Permission & Activities'>
        <p>
          <span className='mr1 text-secondary'>Location Permission:</span>
          <Tooltip
            effect='solid'
            position='top'
            type='dark'
            trigger={
              <span>
                <Tag
                  label={locationPermissionLabel}
                  size={TAG_SIZE.small}
                  tagStyle={TAG_STYLE[locationPermissionTagStyle]}
                />
              </span>
            }>
            {settings?.locationPermission?.info ?? ''}
          </Tooltip>
        </p>
        <p>
          <span className='mr1 text-secondary'>Push Permission:</span>
          <Tooltip
            effect='solid'
            position='top'
            type='dark'
            trigger={
              <span>
                <Tag
                  label={pushPermissionLabel}
                  size={TAG_SIZE.small}
                  tagStyle={TAG_STYLE[pushPermissionTagStyle]}
                />
              </span>
            }>
            {settings?.pushNotificationPermission?.info}
          </Tooltip>
        </p>
        <p>
          <span className='mr1 text-secondary'>Last Ping:</span>
          {getTimeSinceLastAction({ actionAt: lastPingAt })}
        </p>
        <br />
        <p>
          Tracking is <b>{trackingStatus}.</b>
        </p>
        <p>
          <Tooltip
            effect='solid'
            position='top'
            type='dark'
            trigger={lastLabel({ label: 'Tracking Turned On' })}>
            This shows the last time the user turned tracking on.
          </Tooltip>
          {getTimeSinceLastAction({ actionAt: settings?.lastTrackingStartedAt })}
        </p>
        <p>
          <Tooltip
            effect='solid'
            position='top'
            type='dark'
            trigger={lastLabel({ label: 'Tracking Paused' })}>
            This shows the last time the user paused tracking.
          </Tooltip>
          {getTimeSinceLastAction({ actionAt: settings?.lastTrackingPausedAt })}
        </p>
        <br />
        <p>
          App is likely <b>{getPrettyStatusLabel({ status: settings.likelyStatus })}.</b>
        </p>
        <p>
          <Tooltip
            effect='solid'
            position='top'
            type='dark'
            trigger={lastLabel({ label: 'Foregound' })}>
            This shows the last time the user opened the app.
          </Tooltip>
          {getTimeSinceLastAction({ actionAt: settings?.lastForegroundAt })}
        </p>
        <p>
          <Tooltip
            effect='solid'
            position='top'
            type='dark'
            trigger={lastLabel({ label: 'Background' })}>
            This shows the last time the user put the app in the background. If empty, app is still
            on.
          </Tooltip>
          {getTimeSinceLastAction({ actionAt: settings?.lastBackgroundAt })}
        </p>
        <p>
          <Tooltip
            effect='solid'
            position='top'
            type='dark'
            trigger={lastLabel({ label: 'Turned Off' })}>
            This shows how long ago did the user turned our app off (or scrolled it up). This is not
            reliable unless set.
          </Tooltip>
          {getTimeSinceLastAction({ actionAt: settings?.turnedOffAt })}
        </p>
      </Card>
    )
  }
)
